import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, InformationBox, useToast } from '@app/core';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useLogout, useMe, usePublicSession, useTaskSetting } from '@app/api';
import { LoginParticipantContent } from '@app/auth';

const useStyles = createUseStyles((theme: Theme) => ({
  message: {
    ...theme.fonts.caption,
  },
  h1: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
  },
  container: {
    width: theme.maxContentWidth.largeForm,
    margin: '0 auto',
    padding: theme.marginBase * 2,
    marginTop: theme.marginBase * 4,
    maxWidth: '100%',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
    gap: theme.marginBase,
  },
}));

export const StudyFinish = () => {
  const { id } = useParams();
  const toast = useToast();
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: unmoderatedTaskSetting } = useTaskSetting(id);
  const { data: session } = usePublicSession(unmoderatedTaskSetting?.sessionId);
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: logout } = useLogout({ noRedirect: true });
  const { data: me } = useMe(true);

  const [error, setError] = useState<string | null>(
    new URLSearchParams(location.search).get('error'),
  );

  const markAsCompleted = async () => {
    return {} as any;
  };

  const uid = me?.id;
  useEffect(() => {
    (async () => {
      if (uid) {
        try {
          const task = await markAsCompleted();
          if (task) {
            navigate(`/p/${task.participationId}`);
          } else {
            navigate(`/`);
          }
        } catch (e) {
          toast.exception(e);
          setError('not-found');
        }
      }
    })();
  }, [uid, id, markAsCompleted, logout, navigate, toast]);

  if (error === 'not-found') {
    return (
      <div className={classes.container}>
        <h1 className={classes.h1}>
          <Trans i18nKey="auth.login.action.studyFinish.titleError" />
        </h1>
        <InformationBox variant="danger">
          <div className={classes.message}>
            <Trans
              i18nKey="auth.login.action.studyFinish.error"
              components={{
                b: <b />,
              }}
              values={{
                study: session?.title || '',
              }}
            ></Trans>
          </div>
        </InformationBox>
        <div className={classes.buttonsContainer}>
          <Button small to="/" color="ink" variant="muted">
            <Trans i18nKey="auth.login.action.studyFinish.mySpace"></Trans>
          </Button>
          <Button
            small
            onClick={async () => {
              await logout();
              setError(null);
            }}
          >
            <Trans i18nKey="auth.login.action.studyFinish.buttonError"></Trans>
          </Button>
        </div>
      </div>
    );
  } else {
    return (
      <div className={classes.container}>
        <LoginParticipantContent title={t('auth.login.title')}>
          <InformationBox>
            <div className={classes.message}>
              <Trans
                i18nKey="auth.login.action.studyFinish.description"
                components={{
                  b: <b />,
                }}
                values={{
                  study: session?.title || '',
                }}
              ></Trans>
            </div>
          </InformationBox>
        </LoginParticipantContent>
      </div>
    );
  }
};
